import React, { useCallback, useMemo } from 'react';

import { type EntryPoint, type EntryPointComponent } from 'react-relay';

import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { ModalTrigger, type ModalTriggerProps } from '@atlassian/entry-points/modal-trigger';

import { DialogContext } from '../../../analytics/dialog-context';
import { useEntrypointErrorAnalytics } from '../../../analytics/use-entrypoint-error-analytics';
import { useOnCloseDialog } from '../../../analytics/use-on-close-dialog';
import { EditionAwarenessButton } from '../../edition-awareness-button';

import { AutoOpenModalTrigger } from './auto-open-modal-entry-point';
import type { DialogConfig } from './types';

type AnyEntryPoint = EntryPoint<EntryPointComponent<any, any, any, any>, any>;

type ModalEntryPointInnerProps<T> = Omit<ModalTriggerProps<T>, 'children'>;
type ModalEntryPointProps<T> = ModalEntryPointInnerProps<T> & {
	dialogConfig: DialogConfig;
	shouldAutoOpen?: boolean;
};

export function ModalEntryPoint<TEntryPoint extends AnyEntryPoint>(
	props: ModalEntryPointProps<TEntryPoint>,
): React.JSX.Element {
	const { dialogConfig, ...rest } = props;

	return (
		<DialogContext.Provider value={dialogConfig}>
			<AnalyticsContext data={dialogConfig}>
				<ModalEntryPointInner<TEntryPoint> {...rest} />
			</AnalyticsContext>
		</DialogContext.Provider>
	);
}

function ModalEntryPointInner<TEntryPoint extends AnyEntryPoint>(
	props: ModalEntryPointInnerProps<TEntryPoint> & { shouldAutoOpen?: boolean },
) {
	const { shouldAutoOpen, entryPointProps, onError, ...rest } = props;

	const onEntrypointError = useEntrypointErrorAnalytics();

	const onErrorCallback = useCallback(
		({ error, errorInfo }: { error: Error; errorInfo: React.ErrorInfo }) => {
			onEntrypointError({ error, errorInfo });
			onError?.({ error, errorInfo });
		},
		[onEntrypointError, onError],
	);

	const onCloseDialog = useOnCloseDialog();

	const onClose = useCallback(
		(extraInformation?: unknown) => {
			let attributes;
			if (
				extraInformation &&
				typeof extraInformation === 'object' &&
				'triggeredBy' in extraInformation
			) {
				attributes = {
					triggeredBy: extraInformation.triggeredBy,
				};
			}
			onCloseDialog(attributes);
			entryPointProps?.onClose?.();
		},
		[entryPointProps, onCloseDialog],
	);

	const finalEntryPointProps = useMemo(
		() => ({ ...entryPointProps, onClose }) as typeof entryPointProps,
		[entryPointProps, onClose],
	);

	// If we're auto opening, we can handle this setting here
	const Trigger = shouldAutoOpen ? AutoOpenModalTrigger : ModalTrigger;

	return (
		<ModalContextProvider>
			<Trigger<TEntryPoint>
				{...rest}
				entryPointProps={finalEntryPointProps}
				onError={onErrorCallback}
				shouldAutoOpen={shouldAutoOpen}
				preloadOnFocus={true}
			>
				{({ ref }) => {
					return <EditionAwarenessButton ref={ref as unknown as React.Ref<HTMLButtonElement>} />;
				}}
			</Trigger>
		</ModalContextProvider>
	);
}
